import { Icons } from '@/src/modules/components/Icons';
import { useFormat } from '@/src/infrastructure/lib/format';
import { useInvitationHistory } from '../hooks/useInvitations';

const ACTION_LABEL: Record<string, string> = {
  CREATED: 'Created',
  UPDATED: 'Updated',
  PUBLISHED: 'Published',
  SHARED: 'Shared',
  VIEWED: 'Viewed',
  RSVP_RECEIVED: 'RSVP received',
  EXPIRED: 'Expired',
  ARCHIVED: 'Archived',
  RESTORED: 'Restored',
  CANCELLED: 'Cancelled',
  SUSPENDED: 'Suspended',
  UNSUSPENDED: 'Unsuspended',
  REPORTED: 'Reported',
  DELETED: 'Deleted',
};

interface Props {
  invitationId: string | null;
  invitationName?: string;
  onClose: () => void;
}

/**
 * Vertical timeline of lifecycle + activity events. The list is small
 * (capped at 200 server-side), so we render in a single scrollable panel
 * rather than virtualising.
 */
export function InvitationHistoryModal({ invitationId, invitationName, onClose }: Props) {
  const { entries, loading, error } = useInvitationHistory(invitationId);
  const fmt = useFormat();

  if (!invitationId) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-surface w-full max-w-lg rounded-3xl shadow-xl overflow-hidden flex flex-col max-h-[80vh]">
        <header className="px-6 py-4 flex items-center justify-between border-b border-outline-variant/30">
          <div>
            <p className="text-xs uppercase tracking-wider text-on-surface-variant font-bold">Timeline</p>
            <h2 className="text-lg font-bold text-on-surface line-clamp-1">
              {invitationName ?? 'Invitation history'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-surface-low text-on-surface-variant"
          >
            <Icons.X size={20} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && (
            <div className="py-12 flex justify-center">
              <div className="w-8 h-8 rounded-full border-4 border-primary border-t-transparent animate-spin" />
            </div>
          )}
          {!loading && error && (
            <div className="text-status-failed font-medium text-sm">{error}</div>
          )}
          {!loading && !error && entries && entries.length === 0 && (
            <div className="text-on-surface-variant text-sm text-center py-12">
              No events recorded yet.
            </div>
          )}
          {!loading && !error && entries && entries.length > 0 && (
            <ol className="relative border-l-2 border-outline-variant/40 pl-5 space-y-5">
              {entries.map((entry) => (
                <li key={entry.id} className="relative">
                  <span className="absolute -left-[27px] top-1 w-3 h-3 rounded-full bg-primary border-2 border-surface shadow" />
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="font-bold text-on-surface text-sm">
                      {ACTION_LABEL[entry.action] ?? entry.action}
                    </p>
                    <time className="text-xs text-on-surface-variant tabular-nums">
                      {fmt.dateTime(entry.createdAt)}
                    </time>
                  </div>
                  {Object.keys(entry.metadata).length > 0 && (
                    <p className="text-xs text-on-surface-variant mt-1 break-words">
                      {Object.entries(entry.metadata)
                        .map(([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`)
                        .join(' · ')}
                    </p>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
